import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useOAuthProviders } from '../hooks/useOAuthProviders';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, Chrome, Heart, Briefcase, ArrowRight, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { validatePIVA, formatPIVA } from '../components/CrafterBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SignupPage() {
  const location = useLocation();
  const oauthUser = location.state?.oauthUser;
  
  const [step, setStep] = useState(oauthUser ? 2 : 1); // Skip step 1 if OAuth user
  const [email, setEmail] = useState(oauthUser?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState(oauthUser?.name || '');
  const [role, setRole] = useState('');
  const [crafterType, setCrafterType] = useState('');
  const [piva, setPiva] = useState('');
  const [pivaError, setPivaError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthProvider, setOauthProvider] = useState(oauthUser?.provider || null);
  const [oauthLoading, setOauthLoading] = useState(null);
  const { providers: enabledProviders } = useOAuthProviders();
  const { signUp, completeOAuthProfile, signInWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handlePivaChange = (value) => {
    setPiva(value);
    if (value && crafterType === 'professionista') {
      const validation = validatePIVA(value);
      setPivaError(validation.valid ? '' : validation.error);
    } else {
      setPivaError('');
    }
  };

  const passwordsMatch = password === confirmPassword;
  const passwordSpecialRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;
  const passwordChecks = {
    length: password.length >= 8,
    digit: /\d/.test(password),
    special: passwordSpecialRegex.test(password),
  };
  const isPasswordStrong = passwordChecks.length && passwordChecks.digit && passwordChecks.special;
  const canProceedStep1 = email && password && isPasswordStrong && confirmPassword && passwordsMatch && fullName;
  const canProceedStep2 = role !== '';
  const canProceedStep3 = crafterType !== '' && (crafterType === 'hobbista' || (crafterType === 'professionista' && piva && !pivaError));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (role === 'crafter' && !canProceedStep3) {
      toast.error('Completa tutti i campi richiesti');
      return;
    }

    setLoading(true);

    try {
      // Prepare additional metadata for crafters
      const metadata = role === 'crafter' ? {
        crafter_type: crafterType,
        p_iva: crafterType === 'professionista' ? (validatePIVA(piva).cleaned || null) : null,
        tier: 'free',
        proposals_this_month: 0,
        proposals_reset_date: new Date().toISOString()
      } : {};

      let result;
      
      // Check if this is an OAuth user completing their profile
      if (oauthUser) {
        console.log('Completing OAuth profile for:', { email, fullName, role, metadata, oauthUser });
        result = await completeOAuthProfile(
          email, 
          fullName, 
          role, 
          metadata,
          { provider: oauthUser.provider, providerId: oauthUser.providerId }
        );
        console.log('CompleteOAuthProfile result:', result);
      } else {
        // Regular signup with email/password
        console.log('Starting signup with:', { email, fullName, role, metadata });
        result = await signUp(email, password, fullName, role, metadata);
        console.log('SignUp result:', result);
      }
      
      if (result.error) {
        console.error('Registration error object:', result.error);
        // Extract error message properly
        const errorMsg = result.error.message || result.error.msg || result.error.error_description || 
                        (typeof result.error === 'string' ? result.error : JSON.stringify(result.error));
        toast.error(errorMsg);
        return;
      }
      
      if (oauthUser) {
        // Profile completed - use the tokens returned to auto-login
        if (result.data?.access_token && result.data?.refresh_token) {
          toast.success('Profilo completato! Accesso in corso...');
          
          try {
            // Import supabase to set session
            const { supabase } = await import('../lib/supabase');
            const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
              access_token: result.data.access_token,
              refresh_token: result.data.refresh_token
            });
            
            if (!sessionError && sessionData?.session) {
              console.log('Session set successfully after profile completion');
              // Redirect to home
              navigate('/');
              return;
            } else {
              console.log('Session set failed:', sessionError);
            }
          } catch (e) {
            console.log('Session set error:', e);
          }
        }
        
        // Fallback: la sessione non è stata impostata, ma il profilo OAuth è
        // stato completato. Redirect silenzioso — se l'utente non è realmente
        // autenticato, il route guard lo devierà su /login senza toast fuorvianti.
        navigate('/');
      } else {
        // Dopo signup email/password: naviga alla pagina "Controlla la tua email"
        // (più chiara di un semplice toast che sparisce dopo pochi secondi).
        navigate(`/auth/check-email?email=${encodeURIComponent(email)}`);
      }
    } catch (error) {
      console.error('Caught error:', error);
      const errorMsg = error?.message || (typeof error === 'string' ? error : 'Errore durante la registrazione');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // Check if Google is configured in our system
    if (enabledProviders.includes('google')) {
      // Use custom OAuth flow (same as LoginPage)
      try {
        const redirectUri = `${window.location.origin}/auth/callback/google`;
        const response = await fetch(`${API_URL}/api/auth/oauth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
        
        if (response.ok) {
          const data = await response.json();
          // Store state for CSRF verification
          sessionStorage.setItem('oauth_state', data.state);
          // Mark as signup flow so callback knows to create profile
          sessionStorage.setItem('oauth_signup', 'true');
          // Redirect to Google
          window.location.href = data.auth_url;
        } else {
          toast.error('Errore configurazione Google OAuth');
        }
      } catch (error) {
        console.error('Google OAuth error:', error);
        toast.error('Errore durante l\'accesso con Google');
      }
    } else {
      // Fallback to Supabase OAuth
      try {
        const { error } = await signInWithGoogle();
        if (error) throw error;
      } catch (error) {
        toast.error(error.message || 'Errore durante l\'accesso con Google');
      }
    }
  };

  const nextStep = () => {
    if (step === 1 && canProceedStep1) {
      setStep(2);
    } else if (step === 2 && canProceedStep2) {
      if (role === 'crafter') {
        setStep(3);
      } else {
        // Client - submit directly
        handleSubmit({ preventDefault: () => {} });
      }
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4" data-testid="signup-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <img 
                src="/rarifex-logo.png" 
                alt="Rarifex Logo"
                className="h-14 w-auto object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {t('signup')}
            </h1>
            <p className="text-slate-600 text-sm">Inizia il tuo viaggio con Rarifex</p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= s 
                    ? 'bg-shop-primary text-white' 
                    : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-8 h-0.5 ${step > s ? 'bg-shop-primary' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="fullName" className="mb-2 block">Nome completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Mario Rossi"
                      className="pl-10"
                      required
                      data-testid="fullname-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@esempio.com"
                      className="pl-10"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10"
                      required
                      minLength={8}
                      data-testid="password-input"
                    />
                  </div>
                  <ul className="text-xs mt-1 space-y-0.5" data-testid="password-strength-rules">
                    <li className={passwordChecks.length ? 'text-green-600' : 'text-slate-500'}>
                      {passwordChecks.length ? '✓' : '•'} Almeno 8 caratteri
                    </li>
                    <li className={passwordChecks.digit ? 'text-green-600' : 'text-slate-500'}>
                      {passwordChecks.digit ? '✓' : '•'} Almeno un numero
                    </li>
                    <li className={passwordChecks.special ? 'text-green-600' : 'text-slate-500'}>
                      {passwordChecks.special ? '✓' : '•'} Almeno un carattere speciale (es. ! @ # $)
                    </li>
                  </ul>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="mb-2 block">Conferma Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className={`pl-10 ${confirmPassword && !passwordsMatch ? 'border-red-500' : ''}`}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Le password non coincidono
                    </p>
                  )}
                  {confirmPassword && passwordsMatch && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Le password coincidono
                    </p>
                  )}
                </div>

                <Button
                  type="button"
                  onClick={nextStep}
                  disabled={!canProceedStep1}
                  className="w-full bg-shop-primary text-white hover:bg-shop-primary-dark"
                >
                  Continua
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-slate-500">oppure</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleSignIn}
                  className="w-full border-slate-300 hover:bg-slate-50"
                >
                  <Chrome className="w-5 h-5 mr-2" />
                  Continua con Google
                </Button>
              </div>
            )}

            {/* Step 2: Role Selection */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-center mb-4">Come vuoi usare Rarifex?</h2>
                
                <RadioGroup value={role} onValueChange={setRole}>
                  <Card 
                    className={`p-4 cursor-pointer transition-all ${
                      role === 'client' 
                        ? 'border-2 border-shop-primary bg-shop-primary/5' 
                        : 'border border-slate-200 hover:border-shop-primary/50'
                    }`}
                    onClick={() => setRole('client')}
                  >
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="client" id="client" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="client" className="font-medium cursor-pointer">
                          Voglio acquistare
                        </Label>
                        <p className="text-sm text-slate-500 mt-1">
                          Scopri e acquista creazioni artigianali uniche
                        </p>
                        <p className="text-xs text-shop-primary mt-2 italic">
                          Potrai diventare artigiano in seguito
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card 
                    className={`p-4 cursor-pointer transition-all ${
                      role === 'crafter' 
                        ? 'border-2 border-shop-primary bg-shop-primary/5' 
                        : 'border border-slate-200 hover:border-shop-primary/50'
                    }`}
                    onClick={() => setRole('crafter')}
                  >
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="crafter" id="crafter" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="crafter" className="font-medium cursor-pointer">
                          Voglio vendere le mie creazioni
                        </Label>
                        <p className="text-sm text-slate-500 mt-1">
                          Apri la tua vetrina e raggiungi nuovi clienti
                        </p>
                        <p className="text-xs text-shop-accent mt-2 italic">
                          Potrai anche acquistare da altri artigiani
                        </p>
                      </div>
                    </div>
                  </Card>
                </RadioGroup>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Indietro
                  </Button>
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!canProceedStep2 || loading}
                    className="flex-1 bg-shop-primary text-white hover:bg-shop-primary-dark"
                  >
                    {role === 'client' ? (loading ? 'Registrazione...' : 'Registrati') : 'Continua'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Crafter Type Selection */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-center mb-4">Tipologia Seller</h2>
                
                <RadioGroup value={crafterType} onValueChange={(v) => {
                  setCrafterType(v);
                  if (v === 'hobbista') {
                    setPiva('');
                    setPivaError('');
                  }
                }}>
                  <Card 
                    className={`p-4 cursor-pointer transition-all ${
                      crafterType === 'hobbista' 
                        ? 'border-2 border-emerald-500 bg-emerald-50' 
                        : 'border border-slate-200 hover:border-emerald-300'
                    }`}
                    onClick={() => { setCrafterType('hobbista'); setPiva(''); setPivaError(''); }}
                  >
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="hobbista" id="hobbista" className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Heart className="w-5 h-5 text-emerald-600" />
                          <Label htmlFor="hobbista" className="font-medium cursor-pointer">
                            Hobbista
                          </Label>
                          <span className="text-lg">🟢</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Crea per passione, senza P.IVA
                        </p>
                        <ul className="text-xs text-slate-500 mt-2 space-y-1">
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-emerald-500" />
                            Nessuna P.IVA richiesta
                          </li>
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-emerald-500" />
                            Ideale per chi inizia
                          </li>
                        </ul>
                      </div>
                    </div>
                  </Card>

                  <Card 
                    className={`p-4 cursor-pointer transition-all ${
                      crafterType === 'professionista' 
                        ? 'border-2 border-red-500 bg-red-50' 
                        : 'border border-slate-200 hover:border-red-300'
                    }`}
                    onClick={() => setCrafterType('professionista')}
                  >
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="professionista" id="professionista" className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-red-600" />
                          <Label htmlFor="professionista" className="font-medium cursor-pointer">
                            Artigiano Professionista
                          </Label>
                          <span className="text-lg">🔴</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Professionista con P.IVA verificata
                        </p>
                        <ul className="text-xs text-slate-500 mt-2 space-y-1">
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-red-500" />
                            Badge "P.IVA Verificato"
                          </li>
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-red-500" />
                            Massima credibilità
                          </li>
                        </ul>
                      </div>
                    </div>
                  </Card>
                </RadioGroup>

                {/* P.IVA Input for Artigiano */}
                {crafterType === 'professionista' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <Label htmlFor="piva" className="mb-2 block font-medium">
                      Partita IVA <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="piva"
                      type="text"
                      value={piva}
                      onChange={(e) => handlePivaChange(e.target.value)}
                      placeholder="12345678901"
                      className={pivaError ? 'border-red-500' : ''}
                      maxLength={14}
                    />
                    {pivaError && (
                      <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {pivaError}
                      </p>
                    )}
                    {piva && !pivaError && (
                      <p className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        Formato P.IVA valido: {formatPIVA(piva)}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Indietro
                  </Button>
                  <Button
                    type="submit"
                    disabled={!canProceedStep3 || loading}
                    className={`flex-1 text-white ${
                      crafterType === 'hobbista' 
                        ? 'bg-emerald-500 hover:bg-emerald-600' 
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {loading ? 'Registrazione...' : 'Completa Registrazione'}
                  </Button>
                </div>

                <p className="text-xs text-center text-slate-500 mt-4">
                  <Link to="/plans" className="text-shop-primary hover:underline">
                    Scopri tutti i piani e i vantaggi →
                  </Link>
                </p>
              </div>
            )}
          </form>

          <p className="text-center mt-6 text-sm text-slate-600">
            Hai già un account?{' '}
            <Link to="/login" className="text-shop-primary font-medium hover:underline">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
